import {
  ChangeEvent,
  useEffect,
  useRef,
  useState
  } from "react";
import {
  CheckCircle,
  Close,
  Delete,
  Error as ErrorIcon,
  MusicNote
  } from "@mui/icons-material";
import { usePresentationMusic } from "../../hooks/usePresentationMusic";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
} from "@mui/material";

interface MusicUploadDialogProps {
  open: boolean;
  onClose: () => void;
  tournamentId: string;
  categoryId: string;
  presentationId: string;
  presentationName: string;
  hasMusic: boolean; // czy prezentacja już ma muzykę
}

/**
 * Dialog do uploadu muzyki dla prezentacji
 * 
 * Komponent prezentacyjny - logika w hooku usePresentationMusic
 */
export default function MusicUploadDialog({
  open,
  onClose,
  tournamentId,
  categoryId,
  presentationId,
  presentationName,
  hasMusic,
}: MusicUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const previousUploadingRef = useRef<boolean>(false);
  const { music, isUploading, isDeleting, isLoading, uploadMusic, deleteMusic, error } = usePresentationMusic(
    tournamentId,
    categoryId,
    presentationId
  );

  // Reset stanu uploadCompleted gdy dialog się otwiera
  useEffect(() => {
    if (open) {
      setUploadCompleted(false);
      setShowUploadSuccess(false);
      setSelectedFile(null);
    }
  }, [open]);

  // Obsługa zakończenia uploadu i oczekiwania na aktualizację Firestore
  useEffect(() => {
    // Jeśli upload się właśnie zakończył (isUploading zmienia się z true na false) i nie ma błędu
    if (previousUploadingRef.current === true && isUploading === false && !error) {
      setUploadCompleted(true);
      setShowUploadSuccess(true);
    }
    previousUploadingRef.current = isUploading;
  }, [isUploading, error]);

  // Oczekiwanie na aktualizację dokumentu w Firestore po uploadzie
  // Jeśli dokument pojawi się ze statusem "uploaded" lub "processing", oznacza to że upload się powiódł
  useEffect(() => {
    if (uploadCompleted && music && (music.status === "uploaded" || music.status === "processing")) {
      // Dokument został zaktualizowany w Firestore - upload zakończony pomyślnie
      // Reset uploadCompleted po krótkim czasie, aby umożliwić zamknięcie dialogu
      const timer = setTimeout(() => {
        setUploadCompleted(false);
      }, 2000); // 2 sekundy na pokazanie komunikatu sukcesu

      return () => clearTimeout(timer);
    }
  }, [uploadCompleted, music]);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Walidacja rozszerzenia
    const allowedExtensions = [".mp3"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    if (!allowedExtensions.includes(fileExtension)) {
      alert(`Nieprawidłowy format pliku. Dozwolone: ${allowedExtensions.join(", ")}`);
      return;
    }

    // Walidacja rozmiaru (max 20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Plik jest za duży. Maksymalny rozmiar: 20MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setShowUploadSuccess(false); // Reset przed uploadem
    setUploadCompleted(false);
    await uploadMusic(selectedFile);
    // Jeśli upload się powiódł, wyczyść wybór pliku
    if (!error) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleClose = () => {
    // Nie pozwól zamknąć dialogu podczas uploadu
    if (isUploading) {
      return; // Blokuj zamknięcie podczas uploadu
    }

    // Jeśli dokument jest już w Firestore ze statusem "uploaded" lub "processing", 
    // upload się powiódł - zawsze pozwól zamknąć
    if (music && (music.status === "uploaded" || music.status === "processing")) {
      // Pozwól zamknąć - dokument jest już w Firestore, upload zakończony pomyślnie
      setSelectedFile(null);
      setShowUploadSuccess(false);
      setUploadCompleted(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
      return;
    }

    // Nie pozwól zamknąć dialogu jeśli upload się właśnie zakończył i czekamy na aktualizację Firestore
    // (dokument jeszcze nie pojawił się w Firestore)
    if (uploadCompleted && !music) {
      return; // Blokuj zamknięcie - czekamy na pojawienie się dokumentu w Firestore
    }

    // W pozostałych przypadkach pozwól zamknąć
    setSelectedFile(null);
    setShowUploadSuccess(false);
    setUploadCompleted(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        // Blokuj zamknięcie przez kliknięcie poza dialogiem podczas uploadu lub gdy czekamy na aktualizację
        disableEscapeKeyDown={isUploading || (uploadCompleted && (!music || (music.status !== "uploaded" && music.status !== "processing")))}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MusicNote color="primary" />
              <Typography variant="h6">Muzyka dla prezentacji</Typography>
            </Box>
            {/* Ukryj przycisk zamykania podczas uploadu */}
            {!isUploading && (
              <IconButton onClick={handleClose} size="small">
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Prezentacja: <strong>{presentationName}</strong>
            </Typography>

            {/* Jeśli muzyka istnieje (status ready) - pokazuj tylko informację i przycisk usuwania */}
            {music && music.status === "ready" && (
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={async () => {
                      if (window.confirm("Czy na pewno chcesz usunąć ten plik muzyczny?")) {
                        await deleteMusic();
                      }
                    }}
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
                  >
                    {isDeleting ? "Usuwanie..." : "Usuń"}
                  </Button>
                }
              >
                <Typography variant="body2">
                  <strong>Plik muzyczny załadowany:</strong>
                  <br />
                  {music.file.originalName} ({formatFileSize(music.file.size)})
                </Typography>
              </Alert>
            )}

            {/* Jeśli muzyki nie ma - pokazuj formularz uploadu */}
            {(!music || music.status !== "ready") && (
              <>
                {/* Komunikat sukcesu po zakończeniu uploadu */}
                {showUploadSuccess && !isUploading && !error && (
                  <Alert severity="success" icon={<CheckCircle />}>
                    <Typography variant="body2">
                      <strong>Plik został pomyślnie przesłany!</strong>
                      <br />
                      {music && (music.status === "uploaded" || music.status === "processing") ? (
                        "Plik jest teraz przetwarzany w tle. Możesz zamknąć to okno."
                      ) : (
                        "Oczekiwanie na potwierdzenie z serwera..."
                      )}
                    </Typography>
                  </Alert>
                )}

                {/* Status przetwarzania */}
                {music && music.status === "processing" && (
                  <Alert severity="info" icon={<CircularProgress size={20} />}>
                    <Typography variant="body2">
                      Plik jest przetwarzany. Proszę czekać...
                    </Typography>
                  </Alert>
                )}

                {/* Status błędu */}
                {music && music.status === "error" && (
                  <Alert severity="error" icon={<ErrorIcon />}>
                    <Typography variant="body2">
                      Wystąpił błąd podczas przetwarzania pliku. Spróbuj ponownie.
                    </Typography>
                  </Alert>
                )}

                {/* Błąd uploadu */}
                {error && (
                  <Alert severity="error">
                    <Typography variant="body2">{error}</Typography>
                  </Alert>
                )}

                {/* Input pliku */}
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,audio/mpeg"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    id="music-file-input"
                    disabled={
                      isUploading || 
                      isDeleting || 
                      uploadCompleted || // Blokuj jeśli upload się zakończył (nawet jeśli dokument jeszcze nie pojawił się w Firestore)
                      (music !== null && music !== undefined && (music.status === "uploaded" || music.status === "processing"))
                    }
                  />
                  <label htmlFor="music-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={
                        isUploading || 
                        isDeleting || 
                        uploadCompleted || // Blokuj jeśli upload się zakończył
                        (music !== null && music !== undefined && (music.status === "uploaded" || music.status === "processing"))
                      }
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {selectedFile ? "Zmień plik" : "Wybierz plik muzyczny"}
                    </Button>
                  </label>

                  {selectedFile && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Wybrany plik:</strong> {selectedFile.name}
                        <br />
                        <strong>Rozmiar:</strong> {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Dozwolony format: MP3 (maksymalnie 20MB)
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        {/* DialogActions tylko gdy nie ma muzyki (ready) - pokazuj przycisk uploadu */}
        {(!music || music.status !== "ready") && (
          <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!selectedFile || isUploading || isDeleting || uploadCompleted}
              startIcon={isUploading ? <CircularProgress size={16} /> : <MusicNote />}
            >
              {isUploading ? "Przesyłanie..." : "Prześlij muzykę"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

    </>
  );
}
